import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { black200, white, blue300, black600 } from '../theme/colors';
import { defaultFont, miniCaption } from '../theme/typography';
import useOutsideClick from '../helpers/utils/useOutsideClick';

const DropDownStyle = styled.div`
    display: inline-block;
    position: relative;   
    cursor: pointer;
    .menu {
        position: absolute;
        top: 100%;
        ${props => props.menuAlign === 'right' ? css`right: 0;` : css`left: 0;`}
        padding-top: ${props => props.menuStartGap || '6px'};
        z-index: 10005;
    }
`;

const MenuStyle = styled.div`
    width: ${props => props.menuWidth || 'auto'};
    min-width: ${props => props.menuWidth || 'max-content'};
    background: ${white};
    position: relative;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.02);
    overflow: hidden;
    
    ul {
        padding: 6px;
        margin: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 2px;
        
        li {
            width: 100%;
            box-sizing: border-box;
            list-style: none;
            padding: ${props => props.listPadding || '10px 14px'};
            ${defaultFont}
            color: ${black600};
            border-radius: 8px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            white-space: nowrap;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            border-bottom: ${props => props.listBorderLine ? `1px ${props.listBorderLine} ${black200}` : 'none'};
            
            &:hover {
                background-color: #f1f5f9;
                color: ${props => props.activeColor || blue300};
                
                & > svg {
                    color: ${props => props.activeColor || blue300};
                }
            }

            /* Custom red-accent style for logout item */
            &.logout-item {
                &:hover {
                    background-color: #fef2f2;
                    color: #ef4444;

                    & > svg {
                        color: #ef4444;
                    }
                }
            }

            & > svg {
                width: 18px;
                height: 18px;
                margin-right: 10px;
                color: ${black600};
                transition: color 0.2s ease-in-out;
            }

            &.active {
                background-color: #eff6ff;
                color: ${props => props.activeColor || blue300};
                font-weight: 600;
            }
        }
    }
`;

const HeadingStyle = styled.p`
    margin: 0;
    padding: 12px 14px 8px 14px;
    ${miniCaption.bold}
    color: ${black600};
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 4px;
`;

export const DropDown = ({
    children,
    options = [],
    onClickAction = () => {},
    menuWidth,
    activeColor,
    menuAlign = 'left',
    listBorderLine,
    heading,
    listPadding,
    menuStartGap,
    triggerAction = 'hover',
    disabled = false,
    highlightActive = false,
}) => {
    const [value, setValue] = useState({});
    const [isMenuOpen , setMenuOpen ] = useState(false);
    const [isPinned, setIsPinned] = useState(false);

    const onSelect = ({selectedValue}) => {
        if (highlightActive) {
            setValue(selectedValue);
        }

        if (selectedValue.onClick) {
            selectedValue.onClick();
        } else {
            onClickAction && onClickAction(selectedValue);
        }

        setTimeout(() => {
            setMenuOpen(false);
            setIsPinned(false);
        }, 200);
    };

    const dropDownRef = useOutsideClick(() => {
        setMenuOpen(false);
        setIsPinned(false);
    });

    const handleClick = (e) => {
        if (disabled) return;
        // Don't toggle trigger if click originated on a menu item
        if (e.target.closest && e.target.closest('.menu')) {
            return;
        }
        setMenuOpen((prev) => {
            const next = !prev;
            setIsPinned(next);
            return next;
        });
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        if (triggerAction === 'hover') {
            setMenuOpen(true);
        }
    };

    const handleMouseLeave = () => {
        if (disabled) return;
        if (triggerAction === 'hover' && !isPinned) {
            setMenuOpen(false);
        }
    };

    return (
        <DropDownStyle 
            ref={dropDownRef}
            menuStartGap={menuStartGap}
            menuAlign={menuAlign}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {isMenuOpen ? (
                <div className='menu'>
                    <MenuStyle 
                        menuWidth={menuWidth} 
                        activeColor={activeColor}
                        menuAlign={menuAlign}
                        listPadding={listPadding}
                        listBorderLine={listBorderLine}
                    >
                        {heading && <HeadingStyle>{heading}</HeadingStyle>}
                        <ul>
                            {
                                options.map((option, index) => {
                                    const isLogout = option.key === 'logout' || (option.label && option.label.toLowerCase() === 'logout');
                                    
                                    return (
                                        <React.Fragment key={index}>
                                            {isLogout && index > 0 && (
                                                <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 8px' }} />
                                            )}
                                            <li
                                                onClick={() => onSelect({ selectedValue: option })}
                                                className={`${highlightActive && option.key === value.key ? 'active' : ''} ${isLogout ? 'logout-item' : ''}`}
                                            >
                                                {option.icon}
                                                {option.label}
                                            </li>
                                        </React.Fragment>
                                    );
                                })
                            }
                        </ul>
                    </MenuStyle>
                </div>
            ) : null}
        </DropDownStyle>
    );
};
